import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Paper, Stack } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridRow, GridRowProps } from "@mui/x-data-grid/components/GridRow";
import { AnimatePresence, motion } from "motion/react";
import React, { MutableRefObject, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbList } from "react-icons/tb";

import CommonGridTable from "@/components/CommonGridTable";
import { CompanionsColumn } from "@/components/table/CompanionsColumn";
import { PassengerColumn } from "@/components/table/PassengerColumn";
import orderColumn from "@/components/table/orderColumn";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { PlanNotesColumn } from "@/pages/Plannn/components/table/PlanNotesColumn";
import { PlanPickupColumn } from "@/pages/Plannn/components/table/PlanPickupColumn";
import {
    CLINIC_ARRIVAL_TASK_DROP_PREFIX,
    ClinicArrivalDragPayload,
    ClinicArrivalDragPayloadTypes,
    ClinicArrivalOverTargetType,
    ClinicArrivalTaskOverData,
    useClinicArrivalDndDrag,
    useClinicArrivalDndOverTaskId,
} from "@/stores/use-clinic-arrival-dnd";

import { HotelClinicResolvedPassenger, HotelClinicTaskResolved } from "../../../types";
import { ClinicArrivalPassengerTypeColumn } from "../../unAssignedPassengersTable/ClinicArrivalPassengerTypeColumn";
import { ClinicArrivalRouteColumn } from "../../unAssignedPassengersTable/ClinicArrivalRouteColumn";
import HotelPickUpReasonColumn from "../../unAssignedPassengersTable/HotelPickUpReasonColumn";
import DetailActionsColumn from "./DetailActionsColumn";

const DraggableAssignedPassengerRow = React.forwardRef<HTMLDivElement, GridRowProps>(
    function DraggableAssignedPassengerRow(props, outerRef) {
        const isPlaceholder = Boolean(props.row?._isPlaceholder);
        const taskId = String(props.row?._taskId ?? "");
        const uniqueIdentifier = String(props.row?.uniqueIdentifier ?? "");

        const dragData: ClinicArrivalDragPayload = useMemo(
            () => ({
                id: props.row?.id ?? "",
                name: props.row?.name ?? "",
                uniqueIdentifier: uniqueIdentifier,
                type: ClinicArrivalDragPayloadTypes.FROM_TASK,
                taskId: taskId,
                listItemId: props.row?.listItemId ?? "",
            }),
            [props.row?.id, props.row?.listItemId, props.row?.name, uniqueIdentifier, taskId]
        );

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: isPlaceholder ? `__placeholder_task__${taskId}` : uniqueIdentifier,
            disabled: isPlaceholder,
            data: dragData,
        });

        const mergedRef = (el: HTMLDivElement | null) => {
            if (!isPlaceholder) setNodeRef(el);
            if (typeof outerRef === "function") outerRef(el);
            else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        };

        if (isPlaceholder) {
            return (
                <GridRow
                    ref={(el: HTMLDivElement | null) => {
                        if (typeof outerRef === "function") outerRef(el);
                        else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                    }}
                    {...props}
                    style={{
                        ...props.style,
                        opacity: 0.5,
                        backgroundColor: "rgba(20,184,166,0.06)",
                        outline: "1.5px dashed rgba(20,184,166,0.5)",
                        outlineOffset: "-2px",
                        pointerEvents: "none",
                    }}
                />
            );
        }

        return (
            <GridRow
                ref={mergedRef}
                {...props}
                {...attributes}
                {...listeners}
                style={{
                    ...props.style,
                    opacity: isDragging ? 0 : 1,
                    cursor: "grab",
                    transition: isDragging ? undefined : "opacity 150ms ease",
                    userSelect: "none",
                }}
            />
        );
    }
);

type TaskDetailPanelProps = {
    task: HotelClinicTaskResolved;
};

function TasksDetailTable({ task }: TaskDetailPanelProps) {
    const api = useGridApiRef() as MutableRefObject<GridApiPremium>;
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
    const { size } = useWindowDimensions();
    const { t } = useTranslation();

    const targetData: ClinicArrivalTaskOverData = useMemo(
        () => ({
            type: ClinicArrivalOverTargetType.Task,
            taskId: task.id,
            zone: "inner",
        }),
        [task.id]
    );
    const { setNodeRef } = useDroppable({
        id: `${CLINIC_ARRIVAL_TASK_DROP_PREFIX}${task.id}:inner`,
        data: targetData,
    });

    const overTaskId = useClinicArrivalDndOverTaskId();
    const drag = useClinicArrivalDndDrag();
    const highlight = overTaskId === task.id;

    const rows = useMemo<HotelClinicResolvedPassenger[]>(() => {
        const base: HotelClinicResolvedPassenger[] = task.passengers.map((p) => ({ ...p, _taskId: task.id }));

        const hasPlaceholder =
            highlight && drag !== null && !base.some((r) => r.uniqueIdentifier === drag.uniqueIdentifier);

        // Must not reuse a real passenger's `listItemId` / `uniqueIdentifier`: `getRowId` is
        // `row.listItemId`, and duplicating it breaks the DataGrid (rows collapse / all render like the placeholder).
        const placeholderKey = `__dnd_ph__${task.id}`;

        return hasPlaceholder
            ? [
                  ...base,
                  {
                      ...(base[0] ?? ({} as HotelClinicResolvedPassenger)),
                      id: placeholderKey,
                      uniqueIdentifier: placeholderKey,
                      listItemId: placeholderKey,
                      name: drag.name,
                      _isPlaceholder: true,
                  } as HotelClinicResolvedPassenger,
              ]
            : base;
    }, [task.passengers, task.id, highlight, drag]);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                headerClassName: "checkbox-header",
            },
            orderColumn(),
            PassengerColumn({ size, t, raw: false }),
            CompanionsColumn({ t, editable: false }),
            ClinicArrivalRouteColumn({ t }),
            PlanPickupColumn({ t, editable: false }),
            HotelPickUpReasonColumn({ t }),
            ClinicArrivalPassengerTypeColumn({ t }),
            PlanNotesColumn({ t, textClassName: "text-xs text-gray-600" }),
            DetailActionsColumn({ taskId: task.id, allRows: rows, selectedRowIds }),
        ],
        [rows, selectedRowIds, size, t, task.id]
    );

    return (
        <motion.div
            ref={setNodeRef}
            animate={{
                backgroundColor: highlight ? "rgba(20,184,166,0.05)" : "#f8fafc",
            }}
            transition={{ duration: 0.15 }}
            className="w-full py-2 px-3"
            style={{
                boxShadow: highlight
                    ? "inset 2px 0 0 0 rgba(20,184,166,0.7), inset -2px 0 0 0 rgba(20,184,166,0.7), inset 0 -2px 0 0 rgba(20,184,166,0.7)"
                    : "none",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
            }}
        >
            <Stack sx={{ py: 1, boxSizing: "border-box" }} direction="column">
                <Paper
                    elevation={0}
                    sx={{ mx: "auto", p: 1, border: "1px solid #e2e8f0", borderRadius: 2 }}
                    className="w-[98%] lg:w-[95%]"
                >
                    <Stack direction="column" spacing={1}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                                <TbList size={15} />
                                Yolcu Listesi
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            {rows.length === 0 ? (
                                <motion.p
                                    key="empty"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: highlight ? 1 : 0.5 }}
                                    exit={{ opacity: 0 }}
                                    className="py-3 text-center text-xs text-slate-400 italic"
                                >
                                    {highlight ? "Yolcuyu buraya bırakın..." : "Henüz yolcu atanmadı"}
                                </motion.p>
                            ) : (
                                <motion.div
                                    key="grid"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                        <CommonGridTable
                                            api={api}
                                            checkboxSelection
                                            hideFooter
                                            columnHeaderHeight={35}
                                            rows={rows}
                                            allRows={rows}
                                            columns={columns}
                                            loading={false}
                                            disableColumnFilter
                                            disableColumnPinning
                                            disableColumnReorder
                                            disableColumnMenu
                                            onRowSelectionModelChange={setSelectedRowIds}
                                            isRowSelectable={(params) => !params.row._isPlaceholder}
                                            CustomGridRow={
                                                DraggableAssignedPassengerRow as React.ComponentType<GridRowProps>
                                            }
                                            getRowId={(row) => row.listItemId}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Stack>
                </Paper>
            </Stack>
        </motion.div>
    );
}

export default TasksDetailTable;
